/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction, useContext } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { AxiosResponse } from "axios";
import { TFunction } from "i18next";
import { useHomeRoute } from "@/routes/hooks/useHomeRoute";
import { resetAllTablesExceptPaginationAndColumns } from "@/stores/useTableStore";

import { AlertContext } from "../../../context/alert/AlertContextProvider";
import { useAuth } from "../../../context/Auth/AuthProvider";
import LanguageContext from "../../../context/language/LanguageContext";
import { LANGUAGES } from "../../../shared";
import { setStoredLanguage } from "../../../shared/functions/functions";
import { AUTHENTICATION_TYPES } from "../types";

const useLogin = () => {
  const { removeAlert } = useContext(AlertContext);
  const { language } = useContext(LanguageContext);

  const {
    setTokenInfo,
    setErrorMessageFromServer,
    toggle2FaVerification,
    twoFaMethod
  } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const { homeRoute } = useHomeRoute();

  const resetAllPreviousValues = () => {
    resetAllTablesExceptPaginationAndColumns();

    setErrorMessageFromServer(false);
    // should remove unauthorized message if user was not authenticated previously
    removeAlert();
  };

  const display2FaVerificationScreen = (
    response: AxiosResponse<any, any>,
    setToken: Dispatch<SetStateAction<string>>
  ) => {
    toggle2FaVerification(true);
    setErrorMessageFromServer(false);
    setToken(response.data.token);
  };

  const storeTokensAndNavigateToHomepage = (
    response: AxiosResponse<any, any>
  ) => {
    setTokenInfo(response.data);

    i18n.changeLanguage(language);
    setStoredLanguage(language || LANGUAGES.English);

    // reset previous values
    resetAllPreviousValues();

    navigate(homeRoute, { replace: true });
  };

  const generateFormTitle = (is2FaVerification: boolean, t: TFunction) => {
    if (is2FaVerification) {
      return twoFaMethod === AUTHENTICATION_TYPES.Authenticator
        ? t("Security##2FA##title##authentication app")
        : t("Security##2FA##title##authentication email");
    }
    return t("Auth##sign in to tacho");
  };

  const generateVerificationDescription = (t: TFunction) => {
    return twoFaMethod === AUTHENTICATION_TYPES.Authenticator ? (
      t("Auth##account verification description")
    ) : (
      <>
        <div>{t("Auth##account verification description")}</div>{" "}
        <div css={css({ marginTop: "8px" })}>
          {t("Auth##account verification##code valid")}
        </div>
      </>
    );
  };

  const generateCodeinputLabel = (t: TFunction) => {
    return twoFaMethod === AUTHENTICATION_TYPES.Authenticator ||
      twoFaMethod === AUTHENTICATION_TYPES.None
      ? t("Password##six digit code")
      : t("Auth##account verification##code sent to email");
  };

  return {
    display2FaVerificationScreen,
    storeTokensAndNavigateToHomepage,
    generateFormTitle,
    generateVerificationDescription,
    generateCodeinputLabel
  };
};

export default useLogin;
